// ============================================================
// USER MODE — Simple vs Pro shell adaptation
// ============================================================
// Lets the same product serve both a 25-person logistics company
// (Simple) and a Globaltrade-scale fleet operator (Pro) without
// hiding features for the Pro user or overwhelming the Simple user.
//
// Default rules:
//   - Account ≤ 5 sites  → Simple
//   - Account ≥ 10 sites → Pro
//   - 6–9 sites          → Pro (lean toward power; toggleable)
//
// Per-user override stored in localStorage.
// Hidden in Simple mode: Alert rules, Firmware, Audit log, API access,
//                        SLA performance, Cost optimizer, Trends, Groups
// Visible in Simple mode: Dashboard, My service, Terminals, Billing,
//                         Service requests, Help & support

import { createContext, useContext, useState, useEffect } from 'react';
import { getAccountNumber } from './auth.js';

const STORAGE_KEY = 'netcom_connect_user_mode';

const SIMPLE_MODE_ROUTES = new Set([
  '/',                  // Dashboard
  '/service-lines',     // My service
  '/service-lines/:id',
  '/terminals',         // My dishes
  '/terminals/:deviceId',
  '/network',           // Map (still useful for a few sites)
  '/billing',
  '/requests',
  '/requests/new',
  '/add-kit',
  '/support',
  '/alerts',            // Show simplified alerts
  '/team',              // Account owner still needs this
  '/settings',
]);

// Sections to hide from sidebar in Simple mode. They're still
// reachable via URL — we just don't surface them in nav.
const PRO_ONLY_NAV_ITEMS = new Set([
  '/groups',
  '/thresholds',
  '/sla',
  '/trends',
  '/firmware',
  '/audit',
  '/api-access',
  '/optimizer',
  '/speed-test',
  '/reports',
]);

export const UserModeContext = createContext({
  mode: 'pro',
  setMode: () => {},
  isSimple: false,
  isPro: true,
  isProOnlyNavItem: () => false,
});

export function useUserMode() {
  return useContext(UserModeContext);
}

/**
 * Determine the default mode for an account based on its size.
 * Account size comes from the auth session (terminalCount, set when
 * the account is loaded). Fallback to localStorage override if set.
 */
function defaultModeForAccount(terminalCount) {
  if (terminalCount == null) return 'pro';  // safest default before data loads
  if (terminalCount <= 5) return 'simple';
  return 'pro';
}

export function UserModeProvider({ children }) {
  const [mode, setModeState] = useState('pro');

  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored === 'simple' || stored === 'pro') {
        setModeState(stored);
        return;
      }
    } catch { /* ignore */ }
    // No override — pick by account size.
    // The session itself doesn't carry terminalCount today, so we read
    // the enterprise account marker as a proxy.
    const acc = getAccountNumber();
    // Use the alias-aware check so all enterprise demo aliases
    // (ACC-DEMO-CTO-50FLEET-01, ACC-DEMO-AUDIT-01, etc.) default to Pro mode.
    if (acc === 'ACC-CTO-50-FLEET' || (acc && acc.includes('DEMO-CTO')) || (acc && acc.includes('DEMO-AUDIT'))) {
      setModeState('pro');
    } else if (acc === 'ACC-100-200-300' || acc === 'ACC-200-300-400') {
      setModeState('simple');
    } else {
      setModeState('pro');
    }
  }, []);

  function setMode(next) {
    setModeState(next);
    try { localStorage.setItem(STORAGE_KEY, next); } catch { /* ignore */ }
  }

  const value = {
    mode,
    setMode,
    isSimple: mode === 'simple',
    isPro: mode === 'pro',
    isProOnlyNavItem: (to) => PRO_ONLY_NAV_ITEMS.has(to),
  };

  return <UserModeContext.Provider value={value}>{children}</UserModeContext.Provider>;
}

export { PRO_ONLY_NAV_ITEMS, SIMPLE_MODE_ROUTES, defaultModeForAccount };
